import Big from 'big.js';
import { Card, EmptyState, formatMoney, formatNumber, formatPercent } from '@platform/design-system';
import { Lightbulb } from 'lucide-react';
import { usePageContext } from '../usePageContext.ts';
import {
  advertisingPerDeliveredOf,
  breakEvenLeadCostOf,
  deliveryGainOf,
  marginOf,
  moneyOf,
  profitPerDeliveredOf,
  simulate,
  type ParsedInput,
  type Simulation,
} from './profitability.ts';
import styles from './Calculator.module.css';

const SENSITIVITY_POINTS = 5;
const COUNT_DECIMALS = 1;

const FUNNEL_STAGES = ['leads', 'confirmed', 'delivered', 'returned'] as const;

const SPLIT_PARTS = ['productCost', 'delivery', 'returns', 'preparation', 'advertising', 'profit'] as const;
type SplitPart = (typeof SPLIT_PARTS)[number];

const SPLIT_COLORS: Readonly<Record<SplitPart, string>> = {
  productCost: 'var(--color-chart1)',
  delivery: 'var(--color-chart2)',
  returns: 'var(--color-chart3)',
  preparation: 'var(--color-chart4)',
  advertising: 'var(--color-chart5)',
  profit: 'var(--color-chart6)',
};

const ZERO = new Big(0);

const amountOfPart = (simulation: Simulation, part: SplitPart): Big =>
  part === 'profit' && simulation.profit.lt(ZERO) ? ZERO : simulation[part];

const widthOf = (part: Big, whole: Big): string =>
  whole.eq(0) ? '0%' : `${part.div(whole).times(100).toFixed(2)}%`;

export type CalculatorResultsProps = { input: ParsedInput | null; currency: string };

export const CalculatorResults = ({ input, currency }: CalculatorResultsProps) => {
  const { t, locale } = usePageContext();
  if (input === null)
    return (
      <Card title={t('calculator.resultTitle')}>
        <EmptyState title={t('calculator.incomplete')} description={t('calculator.incompleteHelp')} />
      </Card>
    );
  const simulation = simulate(input);
  const money = (value: Big | null) =>
    value === null ? '—' : formatMoney({ amount: moneyOf(value), currency }, locale);
  const count = (value: Big) => formatNumber(value.toNumber(), locale, COUNT_DECIMALS);
  const margin = marginOf(simulation);
  const profitable = simulation.profit.gte(0);
  const breakEven = breakEvenLeadCostOf(simulation);
  const splitTotal = SPLIT_PARTS.reduce((total, part) => total.plus(amountOfPart(simulation, part)), ZERO);
  return (
    <div className={styles['results']} aria-live="polite">
      <section
        className={styles['hero']}
        data-tone={profitable ? 'gain' : 'loss'}
        aria-label={t('calculator.resultTitle')}
      >
        <span className={styles['heroLabel']}>
          {profitable ? t('calculator.profit') : t('calculator.loss')}
        </span>
        <strong className={styles['heroValue']}>{money(simulation.profit)}</strong>
        <span className={styles['heroDetail']}>
          {t('calculator.perDelivered', { amount: money(profitPerDeliveredOf(simulation)) })}
          {margin === null
            ? null
            : ` · ${t('calculator.margin', { value: formatPercent(margin.toNumber(), locale) })}`}
        </span>
      </section>
      <Card title={t('calculator.funnelTitle')}>
        <ul className={styles['funnel']}>
          {FUNNEL_STAGES.map((stage) => (
            <li key={stage} className={styles['stage']}>
              <span>{t(`calculator.funnel_${stage}`)}</span>
              <span className={styles['bar']} aria-hidden="true">
                <span
                  style={{ inlineSize: widthOf(simulation[stage], simulation.leads) }}
                  data-stage={stage}
                />
              </span>
              <strong className={styles['figure']}>{count(simulation[stage])}</strong>
            </li>
          ))}
        </ul>
      </Card>
      <Card title={t('calculator.splitTitle', { revenue: money(simulation.revenue) })}>
        <div className={styles['split']} aria-hidden="true">
          {SPLIT_PARTS.map((part) => (
            <span
              key={part}
              style={{
                inlineSize: widthOf(amountOfPart(simulation, part), splitTotal),
                background: SPLIT_COLORS[part],
              }}
            />
          ))}
        </div>
        <dl className={styles['legend']}>
          {SPLIT_PARTS.map((part) => (
            <div key={part}>
              <dt>
                <span
                  className={styles['swatch']}
                  style={{ background: SPLIT_COLORS[part] }}
                  aria-hidden="true"
                />
                {t(`calculator.split_${part}`)}
              </dt>
              <dd className={styles['figure']}>{money(amountOfPart(simulation, part))}</dd>
            </div>
          ))}
        </dl>
      </Card>
      <dl className={styles['keyFigures']}>
        <div>
          <dt>{t('calculator.advertisingPerDelivered')}</dt>
          <dd className={styles['figure']}>{money(advertisingPerDeliveredOf(simulation))}</dd>
        </div>
        <div>
          <dt>{t('calculator.breakEven')}</dt>
          <dd className={styles['figure']}>{money(breakEven)}</dd>
        </div>
      </dl>
      <p className={styles['tip']}>
        <Lightbulb size={20} aria-hidden="true" />
        <span>
          {t('calculator.tipGain', {
            points: SENSITIVITY_POINTS,
            gain: money(deliveryGainOf(input, SENSITIVITY_POINTS)),
          })}
          {breakEven === null ? null : ` ${t('calculator.tipBreakEven', { amount: money(breakEven) })}`}
        </span>
      </p>
    </div>
  );
};
